/**
 * Document Encryption Service - Enterprise Plan Only
 * 
 * Implements envelope encryption (DEK/KEK) for bank-grade security.
 * This service is ONLY available for Enterprise Plan customers.
 * 
 * Architecture:
 * - DEK (Data Encryption Key): Generated client-side, unique per document
 * - KEK (Key Encryption Key): Managed by Google Cloud KMS, per-organization
 * - Process: Document → Encrypted with DEK → DEK wrapped with KEK → Store both
 * - Security: KMS never sees plaintext document (only the DEK is sent to KMS)
 * 
 * GLBA Reference: 16 CFR 314.4(c)(3)
 * Control: C1 (Client-Side Encryption)
 */

import { getAuth } from 'firebase/auth';

export interface EncryptionMetadata {
  wrappedDEK: string; // Base64-encoded wrapped DEK from KMS
  iv: string; // Base64-encoded initialization vector
  algorithm: string; // Always "AES-256-GCM"
  kmsKeyPath: string; // Path to the KEK used (e.g., "projects/.../locations/.../keyRings/.../cryptoKeys/...")
  kmsKeyVersion?: string; // Version of the KEK used (for key rotation tracking)
}

export interface EncryptedDocument {
  encryptedData: ArrayBuffer; // Document encrypted with DEK
  metadata: EncryptionMetadata;
}

/**
 * Check if the current organization has Enterprise plan and encryption enabled
 */
export function isEncryptionEnabled(): boolean {
  // This will be called from React components, so we need to check via context
  // For non-React contexts, use subscriptionService.hasFeature directly
  return false; // Placeholder - will be implemented via hook
}

/**
 * Generate a random DEK (Data Encryption Key) using Web Crypto API
 * 
 * @returns Promise<CryptoKey> - AES-256-GCM key
 */
async function generateDEK(): Promise<CryptoKey> {
  return await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256, // AES-256
    },
    true, // extractable (we need to export it to send to KMS)
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt document using DEK (Data Encryption Key)
 * 
 * @param file - File to encrypt
 * @param dek - Data Encryption Key (CryptoKey)
 * @returns Promise<{ encryptedData: ArrayBuffer, iv: string }>
 */
async function encryptWithDEK(
  file: File,
  dek: CryptoKey
): Promise<{ encryptedData: ArrayBuffer; iv: string }> {
  // Generate random IV (12 bytes for AES-GCM)
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  // Read file as ArrayBuffer
  const fileBuffer = await file.arrayBuffer();
  
  // Encrypt with AES-256-GCM
  const encryptedData = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
    },
    dek,
    fileBuffer
  );
  
  // Convert IV to base64 for storage
  const ivBase64 = btoa(String.fromCharCode(...iv));
  
  return {
    encryptedData,
    iv: ivBase64,
  };
}

/**
 * Get Firebase Functions URL for KMS endpoints
 */
function getKMSFunctionUrl(functionName: string): string {
  const envAny = (import.meta as any).env || {};
  const projectId = envAny.VITE_FIREBASE_PROJECT_ID || 'lemur-476302';
  
  // Check for base URL override
  const base = envAny.VITE_FUNCTIONS_BASE;
  if (base) {
    return `${base}/${functionName}`;
  }
  
  // Auto-detect: check if we're on localhost (use emulator) or production
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.startsWith('192.168.');
  
  if (isLocalhost) {
    return `http://localhost:5001/${projectId}/us-central1/${functionName}`;
  }
  
  // Production: use deployed functions URL
  return `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;
}

/**
 * Get Firebase Auth token for API requests
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Wrap DEK with KMS KEK (Key Encryption Key)
 * 
 * This function calls a server-side API endpoint that uses Google Cloud KMS
 * to wrap (encrypt) the DEK with the organization's KEK.
 * 
 * @param dekMaterial - Raw DEK material (ArrayBuffer)
 * @param organizationId - Organization ID
 * @returns Promise<{ wrappedDEK: string, kmsKeyPath: string, kmsKeyVersion?: string }>
 */
async function wrapDEKWithKMS(
  dekMaterial: ArrayBuffer,
  organizationId: string
): Promise<{ wrappedDEK: string; kmsKeyPath: string; kmsKeyVersion?: string }> {
  // Convert DEK material to base64 for transmission
  const dekBase64 = btoa(String.fromCharCode(...new Uint8Array(dekMaterial)));
  
  // Get auth token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please sign in.');
  }
  
  // Get function URL
  const functionUrl = getKMSFunctionUrl('wrapDek');
  
  // Call server-side API endpoint to wrap DEK with KMS
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      organizationId,
      dekMaterial: dekBase64,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(`Failed to wrap DEK with KMS: ${error.error || error.message || response.statusText}`);
  }
  
  const result = await response.json();
  return {
    wrappedDEK: result.wrappedDEK,
    kmsKeyPath: result.kmsKeyPath,
    kmsKeyVersion: result.kmsKeyVersion,
  };
}

/**
 * Encrypt document before upload using envelope encryption (DEK/KEK)
 * 
 * Process:
 * 1. Generate DEK (Data Encryption Key) - random AES-256-GCM key
 * 2. Encrypt document with DEK using AES-256-GCM
 * 3. Wrap DEK with KMS KEK (Key Encryption Key)
 * 4. Return encrypted document + wrapped DEK + metadata
 * 
 * @param file - File to encrypt
 * @param organizationId - Organization ID (for KMS key lookup)
 * @returns Promise<EncryptedDocument>
 */
export async function encryptDocument(
  file: File,
  organizationId: string
): Promise<EncryptedDocument> {
  // Step 1: Generate DEK
  const dek = await generateDEK();
  
  // Step 2: Encrypt document with DEK
  const { encryptedData, iv } = await encryptWithDEK(file, dek);
  
  // Step 3: Export DEK material
  const dekMaterial = await crypto.subtle.exportKey('raw', dek);
  
  // Step 4: Wrap DEK with KMS KEK
  const { wrappedDEK, kmsKeyPath, kmsKeyVersion } = await wrapDEKWithKMS(
    dekMaterial,
    organizationId
  );
  
  // Step 5: Return encrypted document + metadata
  return {
    encryptedData,
    metadata: {
      wrappedDEK,
      iv,
      algorithm: 'AES-256-GCM',
      kmsKeyPath,
      kmsKeyVersion,
    },
  };
}

/**
 * Unwrap DEK from KMS (server-side operation)
 * 
 * This function calls a server-side API endpoint that uses Google Cloud KMS
 * to unwrap (decrypt) the DEK using the organization's KEK.
 * 
 * @param wrappedDEK - Base64-encoded wrapped DEK
 * @param kmsKeyPath - Path to the KEK used
 * @param kmsKeyVersion - Version of the KEK used (optional)
 * @param organizationId - Organization ID
 * @returns Promise<ArrayBuffer> - Unwrapped DEK material
 */
async function unwrapDEKFromKMS(
  wrappedDEK: string,
  kmsKeyPath: string,
  kmsKeyVersion: string | undefined,
  organizationId: string
): Promise<ArrayBuffer> {
  // Get auth token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please sign in.');
  }
  
  // Get function URL
  const functionUrl = getKMSFunctionUrl('unwrapDek');
  
  // Call server-side API endpoint to unwrap DEK from KMS
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      organizationId,
      wrappedDEK,
      kmsKeyPath,
      kmsKeyVersion,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(`Failed to unwrap DEK from KMS: ${error.error || error.message || response.statusText}`);
  }
  
  const result = await response.json();
  // Convert base64 DEK material back to ArrayBuffer
  const dekBase64 = result.dekMaterial;
  const dekBytes = Uint8Array.from(atob(dekBase64), c => c.charCodeAt(0));
  return dekBytes.buffer;
}

/**
 * Decrypt document after download using envelope encryption (DEK/KEK)
 * 
 * Process:
 * 1. Unwrap (decrypt) DEK from KMS using KEK
 * 2. Import unwrapped DEK
 * 3. Decrypt document with DEK
 * 
 * @param encryptedData - Encrypted document (ArrayBuffer)
 * @param metadata - Encryption metadata (wrappedDEK, iv, etc.)
 * @param organizationId - Organization ID
 * @returns Promise<ArrayBuffer> - Decrypted document
 */
export async function decryptDocument(
  encryptedData: ArrayBuffer,
  metadata: EncryptionMetadata,
  organizationId: string
): Promise<ArrayBuffer> {
  // Step 1: Unwrap DEK from KMS
  const dekMaterial = await unwrapDEKFromKMS(
    metadata.wrappedDEK,
    metadata.kmsKeyPath,
    metadata.kmsKeyVersion,
    organizationId
  );
  
  // Step 2: Import unwrapped DEK
  const dek = await crypto.subtle.importKey(
    'raw',
    dekMaterial,
    { name: 'AES-GCM' },
    false, // not extractable (we don't need to export it again)
    ['decrypt']
  );
  
  // Step 3: Decrypt document with DEK
  const ivArray = Uint8Array.from(atob(metadata.iv), c => c.charCodeAt(0));
  const decryptedData = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ivArray,
    },
    dek,
    encryptedData
  );
  
  return decryptedData;
}

/**
 * Convert ArrayBuffer to Blob (for file download)
 */
export function arrayBufferToBlob(
  arrayBuffer: ArrayBuffer,
  mimeType: string = 'application/octet-stream'
): Blob {
  return new Blob([arrayBuffer], { type: mimeType });
}

/**
 * Convert Blob to File (for file upload)
 */
export function blobToFile(
  blob: Blob,
  fileName: string,
  mimeType?: string
): File {
  return new File([blob], fileName, { type: mimeType || blob.type });
}

